import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RefreshCw, Mail } from 'lucide-react';
import { api } from '../../api/client';
import { Money } from '../../components/Money';
import { StatusBadge } from '../../components/StatusBadge';

interface Detail {
  id: string;
  owner: { id: string; name: string };
  external_reference: string;
  flow: string;
  status: string;
  customer: { name: string; phone: string | null; email: string };
  amount_ore: number;
  description: string;
  metadata: Record<string, unknown> | null;
  callback_url: string | null;
  created_at: string;
  expires_at: string;
  paid_at: string | null;
  refunded_amount_ore: number;
  events: Array<{ event: string; detail: unknown; occurred_at: string }>;
  attempts: Array<{ id: string; vipps_reference: string; vipps_state: string | null; created_at: string }>;
  webhooks: Array<{
    id: string;
    url: string;
    event: string;
    status: string;
    attempts: number;
    last_status: number | null;
    last_error: string | null;
    created_at: string;
  }>;
}

export default function RequestDetail() {
  const { id } = useParams<{ id: string }>();
  const [d, setD] = useState<Detail | null>(null);

  function refresh() {
    if (!id) return;
    api.get<Detail>(`/api/admin/requests/${id}`).then(setD);
  }
  useEffect(refresh, [id]);

  async function syncNow() {
    if (!id) return;
    await api.post(`/api/admin/requests/${id}/sync`);
    refresh();
  }
  async function resend() {
    if (!id) return;
    await api.post(`/api/admin/requests/${id}/resend-receipt`);
    alert('Kvittering lagt i kø');
  }

  if (!d) return <div className="p-6">Laster …</div>;

  return (
    <div className="p-6 max-w-5xl space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-sm text-slate-500">{d.owner.name}</div>
          <h1 className="text-2xl font-semibold">{d.external_reference}</h1>
          <div className="mt-1 flex items-center gap-3">
            <StatusBadge status={d.status} />
            <span className="text-slate-500 text-sm">{d.flow}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={syncNow} className="flex items-center gap-2 bg-white border border-slate-300 rounded px-3 py-2 text-sm hover:bg-slate-50">
            <RefreshCw size={14} /> Synk
          </button>
          <button onClick={resend} className="flex items-center gap-2 bg-white border border-slate-300 rounded px-3 py-2 text-sm hover:bg-slate-50">
            <Mail size={14} /> Send kvittering på nytt
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <Card title="Krav">
          <Row k="Beløp"><Money ore={d.amount_ore} /></Row>
          <Row k="Beskrivelse">{d.description}</Row>
          <Row k="Opprettet">{new Date(d.created_at).toLocaleString('nb-NO')}</Row>
          <Row k="Utløper">{new Date(d.expires_at).toLocaleString('nb-NO')}</Row>
          {d.paid_at && <Row k="Betalt">{new Date(d.paid_at).toLocaleString('nb-NO')}</Row>}
          {d.refunded_amount_ore > 0 && <Row k="Refundert"><Money ore={d.refunded_amount_ore} /></Row>}
        </Card>
        <Card title="Kunde">
          <Row k="Navn">{d.customer.name}</Row>
          <Row k="E-post">{d.customer.email}</Row>
          {d.customer.phone && <Row k="Telefon">{d.customer.phone}</Row>}
        </Card>
      </div>

      <Card title="Hendelser">
        <ul className="text-sm space-y-1">
          {d.events.map((e, i) => (
            <li key={i} className="flex gap-3">
              <span className="text-slate-500 w-44 tabular-nums">{new Date(e.occurred_at).toLocaleString('nb-NO')}</span>
              <span className="font-mono">{e.event}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="Vipps-forsøk">
        <table className="w-full text-sm">
          <thead className="text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left py-1">Tidspunkt</th>
              <th className="text-left py-1">Referanse</th>
              <th className="text-left py-1">State</th>
            </tr>
          </thead>
          <tbody>
            {d.attempts.map((a) => (
              <tr key={a.id} className="border-t border-slate-100">
                <td className="py-1">{new Date(a.created_at).toLocaleString('nb-NO')}</td>
                <td className="py-1 font-mono text-xs">{a.vipps_reference}</td>
                <td className="py-1">{a.vipps_state ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="Webhook-leveranser">
        {d.webhooks.length === 0 ? (
          <div className="text-slate-500 text-sm">Ingen webhooks sendt.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left py-1">Tidspunkt</th>
                <th className="text-left py-1">URL</th>
                <th className="text-left py-1">Event</th>
                <th className="text-left py-1">Status</th>
                <th className="text-left py-1">Forsøk</th>
                <th className="text-left py-1">HTTP</th>
              </tr>
            </thead>
            <tbody>
              {d.webhooks.map((w) => (
                <tr key={w.id} className="border-t border-slate-100">
                  <td className="py-1 text-slate-600">{new Date(w.created_at).toLocaleString('nb-NO')}</td>
                  <td className="py-1 truncate max-w-xs">{w.url}</td>
                  <td className="py-1 font-mono text-xs">{w.event}</td>
                  <td className="py-1">{w.status}</td>
                  <td className="py-1">{w.attempts}</td>
                  <td className="py-1">{w.last_status ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {d.metadata && (
        <Card title="Metadata">
          <pre className="text-xs bg-slate-50 p-3 rounded overflow-auto">{JSON.stringify(d.metadata, null, 2)}</pre>
        </Card>
      )}
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-lg shadow border border-slate-200 p-5">
      <h2 className="font-semibold mb-3">{title}</h2>
      {children}
    </section>
  );
}

function Row({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between py-1 text-sm">
      <span className="text-slate-600">{k}</span>
      <span>{children}</span>
    </div>
  );
}
